import { Home, MapPin, Building2, Trees, Package } from "lucide-react";
import { FormInput, FormSelect, RadioGroup, CheckboxGroup } from "./FormInput";

// Constants
const PROPERTY_TYPE_CARDS = [
  { value: 'Single Family', label: 'House', icon: Home, description: 'Single Family Home' },
  { value: 'Multi-Family', label: 'Multi-Family', icon: Building2, description: 'Duplex, Triplex, Quadplex' },
  { value: 'Vacant Land', label: 'Vacant Land', icon: Trees, description: 'Land, Lots, Acreage' },
  { value: 'Commercial', label: 'Commercial', icon: Package, description: 'Commercial Property' },
];

const HOUSE_SUBTYPES = ['Single Family', 'Duplex', 'Triplex', 'Quadplex', 'Mobile Home', 'Condo/Townhome', 'Other'];
const OCCUPANCY_OPTIONS = ['Owner Occupied', 'Tenant Occupied', 'Vacant'];
const MORTGAGE_OPTIONS = ['Yes', 'No', 'Not Sure'];
const BEDROOM_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, '10+'];
const BATHROOM_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, '5+'];
const ZONING_OPTIONS = ['Residential', 'Commercial', 'Agricultural', 'Mixed Use', 'Unknown'];
const UTILITY_OPTIONS = ['Electric', 'Water', 'Sewer', 'Septic', 'Well', 'None'];
const ROAD_ACCESS_OPTIONS = ['Paved Road', 'Dirt Road', 'Easement Access', 'No Legal Access', 'Unsure'];
const FLOOD_ZONE_OPTIONS = ['Yes', 'No', 'Unsure'];
const HOA_OPTIONS = ['Yes', 'No'];

// Helper to determine selected category from property_type
const getSelectedCategory = (propertyType) => {
  if (propertyType === 'Vacant Land') return 'Vacant Land';
  if (propertyType === 'Commercial') return 'Commercial';
  if (['Duplex', 'Triplex', 'Quadplex'].includes(propertyType)) return 'Multi-Family';
  if (propertyType) return 'Single Family';
  return '';
};

// Sub-component: Property Type Card
const PropertyTypeCard = ({ type, isSelected, onClick }) => {
  const IconComponent = type.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-4 sm:p-6 border-2 transition-all ${
        isSelected 
          ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' 
          : 'border-white/10 text-[#A3A3A3] hover:border-white/30 hover:bg-white/5'
      }`}
      data-testid={`property-type-${type.value.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <IconComponent className={`w-8 h-8 sm:w-10 sm:h-10 mb-2 ${isSelected ? 'text-[#D4AF37]' : 'text-[#666]'}`} />
      <span className={`font-semibold text-sm sm:text-base ${isSelected ? 'text-[#D4AF37]' : 'text-white'}`}>
        {type.label}
      </span>
      <span className="text-xs text-[#666] mt-1 text-center">{type.description}</span>
    </button>
  );
};

// Sub-component: Property Type Selector
const PropertyTypeSelector = ({ selectedCategory, onCategorySelect, propertyType, onChange }) => {
  const isHouseSelected = selectedCategory === 'Single Family' || 
    ['Single Family', 'Mobile Home', 'Condo/Townhome', 'Other'].includes(propertyType);

  return (
    <>
      <div>
        <label className="block text-sm text-[#A3A3A3] mb-3">What type of property are you selling? *</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PROPERTY_TYPE_CARDS.map((type) => (
            <PropertyTypeCard
              key={type.value}
              type={type}
              isSelected={
                selectedCategory === type.value || 
                (type.value === 'Single Family' && isHouseSelected)
              }
              onClick={() => onCategorySelect(type.value)}
            />
          ))}
        </div>
      </div>

      {(selectedCategory === 'Single Family' || selectedCategory === 'Multi-Family') && (
        <RadioGroup
          label="Specific property type"
          name="property_type"
          options={HOUSE_SUBTYPES}
          value={propertyType}
          onChange={onChange}
          required
        />
      )}
    </>
  );
};

// Sub-component: Address Fields
const AddressFields = ({ formData, onChange }) => (
  <>
    <FormInput
      label="Property Address"
      required
      icon={MapPin}
      type="text"
      name="property_address"
      value={formData.property_address}
      onChange={onChange}
      placeholder=""
    />
    
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      <div className="col-span-2 sm:col-span-1">
        <FormInput
          label="City"
          required
          type="text"
          name="city"
          value={formData.city}
          onChange={onChange}
          placeholder=""
        />
      </div>
      <FormInput
        label="State"
        required
        type="text"
        name="state"
        value={formData.state}
        onChange={onChange}
        placeholder=""
      />
      <FormInput
        label="ZIP Code"
        required
        type="text"
        name="zip_code"
        value={formData.zip_code}
        onChange={onChange}
        placeholder=""
      />
    </div>
  </>
);

// Sub-component: Vacant Land Fields
const VacantLandFields = ({ formData, onChange, onCheckboxChange }) => (
  <>
    <div className="border-t border-[#D4AF37]/30 pt-6 mt-6">
      <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
        <Trees className="w-5 h-5" />
        Land Details
      </h3>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormInput
        label="County"
        required
        type="text"
        name="county"
        value={formData.county}
        onChange={onChange}
        placeholder="Mecklenburg"
      />
      <FormInput
        label="Parcel Number (APN/Tax ID)"
        required
        type="text"
        name="parcel_number"
        value={formData.parcel_number}
        onChange={onChange}
        placeholder="123-45-6789"
      />
    </div>

    <FormInput
      label="Acreage"
      required
      type="text"
      name="acreage"
      value={formData.acreage}
      onChange={onChange}
      placeholder="e.g., 5 acres, 0.5 acres, 10,000 sq ft"
    />

    <RadioGroup
      label="Zoning Type"
      name="zoning_type"
      options={ZONING_OPTIONS}
      value={formData.zoning_type}
      onChange={onChange}
      required
    />

    <CheckboxGroup
      label="Utilities Available (Select all that apply)"
      name="utilities_available"
      options={UTILITY_OPTIONS}
      selected={formData.utilities_available || []}
      onChange={onCheckboxChange}
      required
      columns={3}
    />

    <RadioGroup
      label="Road Access"
      name="road_access"
      options={ROAD_ACCESS_OPTIONS}
      value={formData.road_access}
      onChange={onChange}
      required
    />

    <RadioGroup
      label="Is the property in a flood zone?"
      name="flood_zone"
      options={FLOOD_ZONE_OPTIONS}
      value={formData.flood_zone}
      onChange={onChange}
      required
    />

    <FormInput
      label="Annual Property Taxes"
      type="text"
      name="annual_taxes"
      value={formData.annual_taxes}
      onChange={onChange}
      placeholder="Example: $1,200/year or Unknown"
    />

    <RadioGroup
      label="Is there an HOA?"
      name="has_hoa"
      options={HOA_OPTIONS}
      value={formData.has_hoa}
      onChange={onChange}
      required
    />

    <FormInput
      label="GPS Coordinates (Optional)"
      type="text"
      name="gps_coordinates"
      value={formData.gps_coordinates}
      onChange={onChange}
      placeholder="e.g., 35.2271° N, 80.8431° W"
    />
  </>
);

// Sub-component: House/Building Fields
const HouseBuildingFields = ({ formData, onChange }) => (
  <>
    <RadioGroup
      label="Is the property currently occupied?"
      name="occupancy_status"
      options={OCCUPANCY_OPTIONS}
      value={formData.occupancy_status}
      onChange={onChange}
      required
    />

    <div className="grid grid-cols-2 gap-4">
      <FormSelect
        label="Bedrooms"
        required
        name="bedrooms"
        value={formData.bedrooms}
        onChange={onChange}
      >
        <option value="">Select</option>
        {BEDROOM_OPTIONS.map(num => (
          <option key={num} value={num}>{num}</option>
        ))}
      </FormSelect>
      
      <FormSelect
        label="Bathrooms"
        required
        name="bathrooms"
        value={formData.bathrooms}
        onChange={onChange}
      >
        <option value="">Select</option>
        {BATHROOM_OPTIONS.map(num => (
          <option key={num} value={num}>{num}</option>
        ))}
      </FormSelect>
    </div>
  </>
);

// Main Component
const PropertyInfoStep = ({ formData, onChange, onCheckboxChange }) => {
  const isVacantLand = formData.property_type === 'Vacant Land';
  const selectedCategory = getSelectedCategory(formData.property_type);

  const handleCategorySelect = (category) => {
    const categoryDefaults = {
      'Vacant Land': 'Vacant Land',
      'Commercial': 'Commercial',
      'Single Family': 'Single Family',
      'Multi-Family': 'Duplex'
    };
    onChange({ target: { name: 'property_type', value: categoryDefaults[category] } });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-info-step">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Home className="w-5 h-5 text-[#D4AF37]" />
        Property Information
      </h2>
      
      <div className="space-y-6">
        <PropertyTypeSelector
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
          propertyType={formData.property_type}
          onChange={onChange}
        />

        <AddressFields formData={formData} onChange={onChange} />

        {isVacantLand && (
          <VacantLandFields 
            formData={formData} 
            onChange={onChange} 
            onCheckboxChange={onCheckboxChange} 
          />
        )}

        {!isVacantLand && formData.property_type && (
          <HouseBuildingFields formData={formData} onChange={onChange} />
        )}

        {formData.property_type && (
          <RadioGroup
            label={isVacantLand ? "Do you currently owe money on the land?" : "Do you currently owe a mortgage on the property?"}
            name="mortgage_status"
            options={MORTGAGE_OPTIONS}
            value={formData.mortgage_status}
            onChange={onChange}
            required
          />
        )}
      </div>
    </div>
  );
};

export default PropertyInfoStep;
